import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Stack,
  Button,
  TextField,
  IconButton,
  InputAdornment,
} from "@mui/material";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import { SocialButton } from "../components/SocialButton";
import { useAuthActions } from "../hooks/auth/useAuthActions";
import { useAuth } from "../contexts/AuthContext";
import MainLogo from "../components/Logo";
import {
  isWebView,
  isViewRNativeAuthWebView,
  handleWebViewLoginSuccess,
  notifyLoginShellTransitionBegin,
  notifyNativeLoginShellState,
  checkFirebaseAuthAndReturnToken,
  forceExtractFirebaseToken,
} from "../utils/webViewUtils";
import { notifyAuthSuccessToServer } from "../services/webViewService";
import { claimChromeRecommendationToast, shouldShowChromeRecommendationToast } from "../utils/browserSupport";
import designTokens from "../styles/designTokens";
import { authPrimaryButtonSx } from "../styles/authPrimaryButton";
import "../styles/fonts.css";
import Privacy from "../components/Privacy";
import Terms from "../components/Terms";
import Toast from "../components/Toast";
import StudioPolicyConsent from "../components/StudioPolicyConsent";
import {
  INVALID_EMAIL_MESSAGE,
  isSocialSignInRequiredError,
  mapLoginErrorMessage,
} from "../utils/loginAuthErrors";
import { isGuestAuthEnabled } from "../utils/guestAuth";
import {
  precheckEmailPasswordAccount,
  type EmailPasswordPrecheckResponse,
} from "../services/api/auth";
import {
  getNewAccountPasswordPolicyErrorCode,
  isEmbeddedAuthContext,
} from "../services/authService";
import { isValidEmailAddress } from "../utils/emailValidation";
import { studioPolicyConsent, type StudioPolicy } from "../services/api/access";
import { appToast } from "../utils/appToast";

const EMAIL_PRECHECK_TIMEOUT_MS = 15_000;
const EMAIL_PRECHECK_FAILED_MESSAGE =
  "Unable to check your email. Please try again.";

const precheckEmailWithTimeout = async (
  email: string,
): Promise<EmailPasswordPrecheckResponse> => {
  const controller = new AbortController();
  let timeoutId: number | undefined;
  const timeout = new Promise<never>((_resolve, reject) => {
    timeoutId = window.setTimeout(() => {
      controller.abort();
      reject(new Error("email_precheck_timeout"));
    }, EMAIL_PRECHECK_TIMEOUT_MS);
  });

  try {
    return await Promise.race([
      precheckEmailPasswordAccount(email, { signal: controller.signal }),
      timeout,
    ]);
  } finally {
    if (timeoutId !== undefined) window.clearTimeout(timeoutId);
  }
};

/** Figma: Studio code row ↔ social logins `or` 구분선 */
const loginOrDividerLineSx = {
  flex: 1,
  height: "1px",
  backgroundColor: designTokens.fill.text.grey80.dark,
  opacity: 0.35,
  maxWidth: 215,
} as const;

/** Figma TextField 14548:4422 — dark, complete/focus/error states. */
export const authInputSx = {
  "& .MuiOutlinedInput-root": {
    height: 48,
    padding: 0,
    borderRadius: designTokens.numbers.cornerRadius[8],
    color: designTokens.fill.text.common100.dark,
    backgroundColor: designTokens.fill.shape.grey30.dark,
    fontFamily: designTokens.typography.fontFamily.roboto,
    "& > .MuiOutlinedInput-notchedOutline": {
      borderColor: designTokens.stroke.grey40.dark,
      borderWidth: "1px",
    },
    "&:hover > .MuiOutlinedInput-notchedOutline": {
      borderColor: designTokens.stroke.grey40.dark,
      borderWidth: "1px",
    },
    "&.Mui-focused > .MuiOutlinedInput-notchedOutline": {
      borderColor: designTokens.stroke.primary50.dark,
      borderWidth: "1px",
    },
    "&.Mui-error > .MuiOutlinedInput-notchedOutline": {
      borderColor: designTokens.colors.accent.red.light,
      borderWidth: "1px",
    },
    "&.Mui-disabled": {
      backgroundColor: designTokens.fill.shape.grey30.dark,
    },
    "&.Mui-disabled > .MuiOutlinedInput-notchedOutline": {
      borderColor: designTokens.stroke.grey40.dark,
      borderWidth: "1px",
    },
  },
  "& .MuiInputBase-input": {
    boxSizing: "border-box",
    height: 48,
    padding: "10px 16px",
    fontFamily: designTokens.typography.fontFamily.roboto,
    fontSize: 16,
    fontWeight: 400,
    lineHeight: "20px",
    color: designTokens.fill.text.common100.dark,
    WebkitTextFillColor: designTokens.fill.text.common100.dark,
    caretColor: designTokens.fill.text.common100.dark,
    "&::placeholder": {
      color: designTokens.fill.text.grey60.dark,
      WebkitTextFillColor: designTokens.fill.text.grey60.dark,
      opacity: 1,
    },
    "&::selection": {
      color: designTokens.fill.text.common0.dark,
      backgroundColor: designTokens.fill.text.primary50.dark,
    },
  },
  "& .MuiInputAdornment-root": {
    marginRight: "12px",
  },
  "& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus, & input:-webkit-autofill:active":
    {
      WebkitTextFillColor: `${designTokens.fill.text.common100.dark} !important`,
      WebkitBoxShadow: `0 0 0 100px ${designTokens.fill.shape.grey30.dark} inset !important`,
      boxShadow: `0 0 0 100px ${designTokens.fill.shape.grey30.dark} inset !important`,
      caretColor: designTokens.fill.text.common100.dark,
    },
} as const;

const avpLoginTokens = designTokens.semantic.avpAuthentication.login;

/** Figma AVPLogin input — scoped to the native auth WebView only. */
export const avpAuthInputSx = {
  ...authInputSx,
  "& .MuiOutlinedInput-root": {
    ...authInputSx["& .MuiOutlinedInput-root"],
    height: avpLoginTokens.input.height,
    borderRadius: avpLoginTokens.input.radius,
    backgroundColor: avpLoginTokens.input.background,
  },
  "& .MuiInputBase-input": {
    ...authInputSx["& .MuiInputBase-input"],
    height: avpLoginTokens.input.height,
    padding: "16px 20px",
    fontSize: avpLoginTokens.input.fontSize,
    lineHeight: "22px",
  },
} as const;

/** Figma AVPLogin primary action — always enabled except during async work. */
export const avpAuthPrimaryButtonSx = {
  ...authPrimaryButtonSx,
  width: "100%",
  height: avpLoginTokens.button.height,
  borderRadius: avpLoginTokens.button.radius,
  fontSize: avpLoginTokens.button.fontSize,
  lineHeight: "24px",
  backgroundColor: avpLoginTokens.button.background,
} as const;

/** Figma 2990:100813 — Create account secondary action. */
const authSecondaryActionSx = {
  minWidth: 0,
  minHeight: 20,
  p: 0,
  textTransform: "none",
  color: designTokens.fill.text.grey80.dark,
  ...designTokens.typography.styles.viewrStudio.bodyRegular,
  "&:hover": {
    color: designTokens.fill.text.common100.dark,
    backgroundColor: "transparent",
    textDecoration: "underline",
  },
} as const;

const PasswordVisibilityIcon = ({ visible }: { visible: boolean }) => (
  <Box
    component="svg"
    aria-hidden="true"
    viewBox={visible ? "0 0 20.25 14.25" : "0 0 21 21"}
    sx={{ display: "block", width: 21, height: 21, overflow: "visible" }}
  >
    {visible ? (
      <>
        <path
          d="M19.125 7.125C19.125 8.325 15.095 13.125 10.125 13.125C5.155 13.125 1.125 8.325 1.125 7.125C1.125 5.925 5.155 1.125 10.125 1.125C15.095 1.125 19.125 5.925 19.125 7.125Z"
          stroke="#78787C"
          strokeWidth="2.25"
        />
        <path
          d="M12.75 7.125C12.75 7.82119 12.4734 8.48887 11.9812 8.98116C11.4889 9.47344 10.8212 9.75 10.125 9.75C9.42881 9.75 8.76113 9.47344 8.26884 8.98116C7.77656 8.48887 7.5 7.82119 7.5 7.125C7.5 6.42881 7.77656 5.76113 8.26884 5.26884C8.76113 4.77656 9.42881 4.5 10.125 4.5C10.8212 4.5 11.4889 4.77656 11.9812 5.26884C12.4734 5.76113 12.75 6.42881 12.75 7.125Z"
          stroke="#78787C"
          strokeWidth="2.25"
        />
      </>
    ) : (
      <path
        d="M1.64062 3.50391L17.3906 19.2539M9.66131 7.56248C10.1729 7.41082 10.7161 7.40061 11.233 7.53295C11.75 7.66528 12.2214 7.93521 12.5972 8.31404C12.973 8.69288 13.2391 9.16646 13.3673 9.68445C13.4955 10.2024 13.4809 10.7455 13.3252 11.2559M6.96609 5.35256C11.1635 3.26962 15.6847 4.95094 19.3594 10.3945C18.3484 11.8918 17.2735 13.1045 16.1592 14.0328M13.6894 15.5979C9.59044 17.4249 5.21489 15.6905 1.64062 10.3945C2.63484 8.92191 3.69108 7.72392 4.78669 6.80156"
        stroke="#78787C"
        strokeWidth="2.275"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    )}
  </Box>
);

type ToastType = "error" | "notice" | "complete";

interface TextStyle {
  fontSize: string;
  fontWeight: number;
  lineHeight: string;
  letterSpacing: string;
  fontFamily: string;
}

interface ToastState {
  open: boolean;
  type: ToastType;
  message: string;
}

interface LoginPageProps {
  variant?: "default" | "mobile";
  allowGuest?: boolean;
  onAuthenticationStart?: () => void;
  onAuthenticationSuccess?: () => void;
}

type EmailLoginStep = "email" | "password" | "create";
type AuthInputField = "email" | "password" | "confirmPassword";

interface LoginLocationState {
  email?: string;
  step?: EmailLoginStep;
  policy?: StudioPolicy;
}

const LoginPage: React.FC<LoginPageProps> = ({
  variant = "default",
  allowGuest = true,
  onAuthenticationStart,
  onAuthenticationSuccess,
}) => {
  const isMobile = variant === "mobile";
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = (location.state ?? {}) as LoginLocationState;
  const [openTerms, setOpenTerms] = useState(false);
  const [openPrivacy, setOpenPrivacy] = useState(false);
  const [showBrowserRecommendationToast, setShowBrowserRecommendationToast] =
    useState(false);
  const [emailStep, setEmailStep] = useState<EmailLoginStep>(
    locationState.step ?? "email",
  );
  const [email, setEmail] = useState(locationState.email ?? "");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [policyAccepted, setPolicyAccepted] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [focusedInput, setFocusedInput] = useState<AuthInputField | null>(null);
  const [emailSubmitting, setEmailSubmitting] = useState(false);
  const [guestSubmitting, setGuestSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [toastState, setToastState] = useState<ToastState>({
    open: false,
    type: "error",
    message: "",
  });
  const {
    login,
    createPasswordAccount,
    loginGoogle,
    loginApple,
    loginMicrosoft,
    loginGuest,
  } = useAuthActions();
  const { error: authError } = useAuth();

  /**
   * visionOS 앱 내 ViewR WKWebView(`authHandler`) 여부.
   *
   * AVP는 이 화면을 따로 갖지 않고 웹의 `/login`을 그대로 띄운다(AVP-AUTH-003).
   * 그래서 AVP 전용 노출은 전부 이 판별 하나로 갈린다.
   */
  const isNativeAvpWebView = useMemo(() => isViewRNativeAuthWebView(), []);
  const showEmailPasswordForm = true;
  /**
   * AVP에서는 제품명을 `View R`로만 표기한다. AVP는 Studio가 아니라 뷰어이고,
   * 같은 화면을 공유할 뿐이다. 웹은 `View R Studio`를 그대로 유지한다.
   * 표기는 `ViewR`가 아니라 디자인 시스템 브랜드 표기인 `View R`을 쓴다.
   */
  const loginTitle = isNativeAvpWebView ? "View R" : "View R Studio";
  const isEmailPasswordFormValid =
    emailStep === "email"
      ? email.length > 0
      : emailStep === "password"
        ? isValidEmailAddress(email) && password.length > 0
        : isValidEmailAddress(email) &&
          password.length > 0 &&
          confirmPassword.length > 0 &&
          policyAccepted &&
          Boolean(locationState.policy);
  /**
   * Figma AVPLogin(15657:148)의 기본 `signInOptions=true` 상태는 Guest 진입을 포함한다.
   * Studio의 환경별 feature flag는 유지하되, native AVP WebView는 그 전역 flag로
   * Figma/AVP 계약이 사라지지 않도록 독립적으로 노출한다.
   */
  const showGuestLogin =
    allowGuest && !isMobile && (isNativeAvpWebView || isGuestAuthEnabled());

  // WebView 환경 감지
  useEffect(() => {
    const webViewDetected = isWebView();

    // WebView 환경에서 Firebase Auth 상태 확인 (Promise rejection 처리로 Uncaught (in promise) 방지)
    if (webViewDetected && !isNativeAvpWebView) {
      checkFirebaseAuthAndReturnToken().catch((err) => {
        console.warn("WebView Firebase Auth 확인 실패:", err);
      });
      setTimeout(() => {
        checkFirebaseAuthAndReturnToken().catch((err) => {
          console.warn("WebView Firebase Auth 지연 확인 실패:", err);
        });
      }, 3000);
      setTimeout(() => {
        forceExtractFirebaseToken().catch((err) => {
          console.warn("WebView Firebase Auth 강제 추출 실패:", err);
        });
      }, 6000);
    }
  }, [isNativeAvpWebView]);

  useEffect(() => {
    if (!isNativeAvpWebView) return;
    const hasValidationError = Boolean(
      emailError || passwordError || confirmPasswordError,
    );
    const state =
      emailStep === "email"
        ? hasValidationError
          ? "emailError"
          : "email"
        : emailStep === "password"
          ? hasValidationError
            ? "passwordError"
            : "password"
          : hasValidationError
            ? "createError"
            : "create";
    notifyNativeLoginShellState(state);
  }, [
    confirmPasswordError,
    emailError,
    emailStep,
    isNativeAvpWebView,
    passwordError,
  ]);

  useEffect(() => {
    if (shouldShowChromeRecommendationToast() && claimChromeRecommendationToast()) {
      setShowBrowserRecommendationToast(true);
    }
  }, []);

  // 페이지 로드 시 세션 스토리지에서 토스트 메시지 확인
  useEffect(() => {
    const savedToast = sessionStorage.getItem("loginToast");
    if (savedToast) {
      try {
        const toastData = JSON.parse(savedToast) as {
          type?: ToastType;
          message?: string;
        };
        setToastState({
          open: true,
          type: toastData.type || "complete",
          message: toastData.message || "",
        });
        // 토스트 메시지는 한 번만 표시하고 삭제
        sessionStorage.removeItem("loginToast");
      } catch (error) {
        console.error("토스트 메시지 파싱 실패:", error);
        sessionStorage.removeItem("loginToast");
      }
    }
  }, []);

  // 인증 에러 감지 및 토스트 표시
  useEffect(() => {
    if (authError) {
      setToastState({
        open: true,
        type: "error",
        message: mapLoginErrorMessage(authError),
      });
    }
  }, [authError]);

  // 토스트 닫기 핸들러
  const handleToastClose = () => {
    setToastState((prev) => ({ ...prev, open: false }));
  };

  const handleDownloadChrome = () => {
    setShowBrowserRecommendationToast(false);
    window.open(
      "https://www.google.com/chrome",
      "_blank",
      "noopener,noreferrer",
    );
  };

  const openAccessPage = (
    normalizedEmail: string,
    data: { precheck?: EmailPasswordPrecheckResponse; accessStatus?: unknown },
    existingPending = false,
  ): void => {
    const flow = { email: normalizedEmail, ...data, existingPending };
    sessionStorage.setItem("emailFirstAccess", JSON.stringify(flow));
    if (isNativeAvpWebView) {
      notifyLoginShellTransitionBegin(
        new URL("/access", window.location.href).href,
      );
    }
    navigate("/access", { state: flow });
  };

  const handleProviderLogin = async (
    provider: "Google" | "Microsoft" | "Apple",
  ): Promise<void> => {
    try {
      onAuthenticationStart?.();
      if (provider === "Google") await loginGoogle();
      if (provider === "Microsoft") await loginMicrosoft();
      if (provider === "Apple") await loginApple();
      if (isEmbeddedAuthContext()) onAuthenticationSuccess?.();
    } catch (error) {
      const raw = error instanceof Error ? error.message : String(error ?? "");
      setToastState({
        open: true,
        type: "error",
        message: mapLoginErrorMessage(`${provider} sign-in failed: ${raw}`),
      });
    }
  };

  const handleEmailContinue = async (): Promise<void> => {
    const normalizedEmail = email.toLowerCase();
    if (!isValidEmailAddress(normalizedEmail)) {
      setEmailError(INVALID_EMAIL_MESSAGE);
      return;
    }

    try {
      setEmailSubmitting(true);
      setEmailError("");
      const precheck = await precheckEmailWithTimeout(normalizedEmail);
      if (precheck.nextAction === "password_login") {
        setEmail(normalizedEmail);
        setEmailStep("password");
        return;
      }
      if (precheck.nextAction === "social_login") {
        const provider = precheck.assignedProvider || precheck.accountType;
        if (provider === "google") await handleProviderLogin("Google");
        else if (provider === "microsoft")
          await handleProviderLogin("Microsoft");
        else if (provider === "apple") await handleProviderLogin("Apple");
        else
          setEmailError(
            "Use the sign-in method assigned by your administrator.",
          );
        return;
      }
      if (precheck.nextAction === "request_pending") {
        openAccessPage(normalizedEmail, { precheck }, true);
        return;
      }
      if (precheck.nextAction === "access_approved") {
        openAccessPage(normalizedEmail, { precheck });
        return;
      }
      if (precheck.nextAction === "request_access") {
        openAccessPage(normalizedEmail, { precheck });
        return;
      }
      setEmailError("Contact your View R administrator to use this email.");
    } catch (error) {
      const raw = error instanceof Error ? error.message : String(error ?? "");
      if (raw.includes("request_already_pending")) {
        try {
          const precheck = await precheckEmailWithTimeout(normalizedEmail);
          openAccessPage(normalizedEmail, { precheck }, true);
        } catch {
          setEmailError(EMAIL_PRECHECK_FAILED_MESSAGE);
        }
        return;
      }
      setEmailError(EMAIL_PRECHECK_FAILED_MESSAGE);
    } finally {
      setEmailSubmitting(false);
    }
  };

  const handleExistingPasswordSignIn = async (): Promise<void> => {
    if (!password) {
      setPasswordError("Enter your password.");
      return;
    }
    try {
      setEmailSubmitting(true);
      onAuthenticationStart?.();
      await login(email.toLowerCase(), password);
      onAuthenticationSuccess?.();
    } catch (error) {
      const errorCode = (error as { code?: unknown } | null)?.code;
      const raw = [
        typeof errorCode === "string" ? errorCode : "",
        error instanceof Error ? error.message : String(error ?? ""),
      ]
        .filter(Boolean)
        .join(": ");
      const mappedMessage = mapLoginErrorMessage(
        `Email/password sign-in failed: ${raw}`,
      );
      const message =
        isNativeAvpWebView &&
        mappedMessage === "The email or password is incorrect."
          ? "Incorrect password. Please check your password and try again."
          : mappedMessage;
      if (isSocialSignInRequiredError(error)) setEmailError(message);
      else setPasswordError(message);
    } finally {
      setEmailSubmitting(false);
    }
  };

  const handleCreatePasswordAccount = async (): Promise<void> => {
    setPasswordError("");
    setConfirmPasswordError("");
    const policyError = getNewAccountPasswordPolicyErrorCode(email, password);
    if (policyError) {
      const mappedMessage = mapLoginErrorMessage(policyError);
      setPasswordError(
        isNativeAvpWebView &&
          mappedMessage === "Password cannot contain your email ID."
          ? "Password cannot contain your email address."
          : mappedMessage,
      );
      return;
    }
    if (password !== confirmPassword) {
      setConfirmPasswordError(
        isNativeAvpWebView
          ? "Passwords don't match. Please try again."
          : "Passwords do not match.",
      );
      return;
    }
    if (!policyAccepted || !locationState.policy) return;
    try {
      setEmailSubmitting(true);
      onAuthenticationStart?.();
      const response = await createPasswordAccount(
        email.toLowerCase(),
        password,
        studioPolicyConsent(locationState.policy),
      );
      onAuthenticationSuccess?.();
      sessionStorage.removeItem("emailFirstAccess");
      if (isNativeAvpWebView) {
        if (!response.token || !response.user) {
          throw new Error("Approved account token was not returned.");
        }
        const webViewUserInfo = {
          uid: response.user.uid,
          email: response.user.email,
          displayName: response.user.displayName,
          provider: "email",
          token: response.token,
        };
        handleWebViewLoginSuccess(response.token, webViewUserInfo);
        try {
          await notifyAuthSuccessToServer(response.token, webViewUserInfo);
        } catch (notificationError) {
          console.warn(
            "AVP account creation server notification failed:",
            notificationError,
          );
        }
        return;
      }
      if (onAuthenticationSuccess) return;
      appToast("Account created successfully.", "complete");
      navigate("/home", { replace: true });
    } catch (error) {
      const errorCode = (error as { code?: unknown } | null)?.code;
      const raw = [
        typeof errorCode === "string" ? errorCode : "",
        error instanceof Error ? error.message : String(error ?? ""),
      ]
        .filter(Boolean)
        .join(": ");
      const message = mapLoginErrorMessage(`Account creation failed: ${raw}`);
      if (errorCode === "auth/invalid-email") setEmailError(message);
      else setPasswordError(message);
    } finally {
      setEmailSubmitting(false);
    }
  };

  const handleEmailFormSubmit = async (
    event: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    event.preventDefault();
    setEmailError("");
    if (emailStep === "email") await handleEmailContinue();
    else if (emailStep === "password") await handleExistingPasswordSignIn();
    else await handleCreatePasswordAccount();
  };

  const handleLoginGoogle = async () => handleProviderLogin("Google");
  const handleLoginApple = async () => handleProviderLogin("Apple");
  const handleLoginMicrosoft = async () => handleProviderLogin("Microsoft");

  const handleLoginGuest = async () => {
    if (!showGuestLogin || guestSubmitting) return;

    setGuestSubmitting(true);
    try {
      await loginGuest();
    } finally {
      setGuestSubmitting(false);
    }
  };

  const providerLoginButtons = (
    <Box
      data-testid={isNativeAvpWebView ? "avp-social-login-options" : undefined}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: isNativeAvpWebView ? `${avpLoginTokens.social.gap}px` : 2,
      }}
    >
      {[
        {
          label: "Continue with Google",
          image: "/Images/Icons/Google.png",
          onClick: handleLoginGoogle,
        },
        {
          label: "Continue with Microsoft",
          image: "/Images/Icons/Microsoft.png",
          onClick: handleLoginMicrosoft,
        },
        {
          label: "Continue with Apple",
          image: "/Images/Icons/Social Login_apple_light.png",
          onClick: handleLoginApple,
        },
      ].map((provider) => (
        <Button
          key={provider.label}
          aria-label={provider.label}
          onClick={provider.onClick}
          sx={{
            minWidth: 0,
            width: isMobile
              ? 72
              : isNativeAvpWebView
                ? avpLoginTokens.social.size
                : 80,
            height: isMobile ? 58 : avpLoginTokens.social.size,
            p: 0,
            border: "1px solid rgba(255,255,255,.13)",
            borderRadius: 999,
            bgcolor: isNativeAvpWebView
              ? avpLoginTokens.social.background
              : designTokens.fill.shape.grey30.dark,
            "&:hover": {
              bgcolor: "#333337",
              borderColor: "rgba(255,255,255,.3)",
              transform: "translateY(-1px)",
            },
          }}
        >
          <Box
            component="img"
            src={provider.image}
            alt=""
            sx={{ width: 24, height: 24, objectFit: "contain" }}
          />
        </Button>
      ))}
    </Box>
  );

  return (
    <Box
      data-testid="login-page"
      data-variant={variant}
      sx={{
        height: "100vh",
        minHeight: "100dvh",
        bgcolor: designTokens.fill.background.grey20.dark,
        color: "text.primary",
        position: "relative",
        overflowY: isNativeAvpWebView ? "hidden" : "auto",
      }}
    >
      {/* 상단 로고 영역 */}
      <Box
        sx={{
          height: isMobile ? 78 : 98,
          alignItems: "center",
          justifyContent: "space-between",
          px: { xs: 3, sm: 5 },
          pt: "env(safe-area-inset-top, 0px)",
          boxSizing: "border-box",
          /**
           * AVP는 로고가 장식이고 창 전체가 로그인 화면이므로 레이아웃 높이를 차지하지 않게 띄운다.
           * 헤더가 98px을 먹으면 아래 콘텐츠가 그만큼 내려앉아 창 중앙과 어긋난다.
           */
          display: isNativeAvpWebView ? "none" : "flex",
        }}
      >
        <MainLogo size={48} />
      </Box>

      {/* 중앙 콘텐츠 */}
      <Box
        display="flex"
        flexDirection="column"
        justifyContent={isMobile ? "flex-start" : "center"}
        alignItems="center"
        /**
         * AVP는 헤더가 absolute라 높이를 빼지 않는다. 또한 `100%`는 부모의 `100vh`를 따라가는데
         * WebView의 vh가 실제 보이는 높이보다 크면 콘텐츠가 아래로 밀리고 마지막 요소(Guest)가
         * 스크롤 밖으로 잘린다. 실제 표시 높이를 따르는 `100dvh`를 쓴다.
         */
        minHeight={
          isMobile
            ? "calc(100dvh - 78px)"
            : isNativeAvpWebView
              ? "100dvh"
              : "calc(100% - 98px)"
        }
        px={
          isMobile
            ? 3
            : isNativeAvpWebView
              ? `${avpLoginTokens.canvas.paddingX}px`
              : 2
        }
        py={
          isMobile
            ? "clamp(128px, calc((100dvh - 416px) / 2), 214px)"
            : isNativeAvpWebView
              ? `${avpLoginTokens.canvas.paddingY}px`
              : undefined
        }
        pb={
          isMobile ? "calc(24px + env(safe-area-inset-bottom, 0px))" : undefined
        }
        boxSizing="border-box"
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: isMobile
              ? "48px"
              : isNativeAvpWebView
                ? `${avpLoginTokens.content.sectionGap}px`
                : designTokens.numbers.gap[64],
            width: isMobile
              ? "min(342px, 100%)"
              : isNativeAvpWebView
                ? `min(${avpLoginTokens.content.containerWidth}px, 100%)`
                : `min(${avpLoginTokens.content.width}px, 100%)`,
            boxSizing: "border-box",
          }}
        >
          {/* 타이틀 */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              color: designTokens.fill.text.grey90.dark,
              textAlign: "center",
              width: "100%",
              lineHeight: 0,
            }}
          >
            {isMobile ? (
              <>
                <Typography
                  sx={{
                    m: 0,
                    fontFamily: '"Roboto", sans-serif',
                    fontSize: "24px",
                    fontWeight: 500,
                    lineHeight: "28px",
                    letterSpacing: 0,
                    color: designTokens.fill.text.grey90.dark,
                  }}
                >
                  Welcome to
                </Typography>
                <Typography
                  component="h1"
                  sx={{
                    m: 0,
                    mt: "4px",
                    fontFamily: '"Roboto", sans-serif',
                    fontSize: "36px",
                    fontWeight: 600,
                    lineHeight: "43px",
                    letterSpacing: 0,
                    color: designTokens.fill.text.common100.dark,
                  }}
                >
                  {loginTitle}
                </Typography>
              </>
            ) : isNativeAvpWebView ? (
              <>
                <Typography
                  sx={{
                    m: 0,
                    fontFamily: designTokens.typography.fontFamily.roboto,
                    fontSize: `${avpLoginTokens.brand.welcomeSize}px`,
                    fontWeight: 700,
                    lineHeight: "34px",
                    color: designTokens.fill.text.grey90.dark,
                  }}
                >
                  Welcome to
                </Typography>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="center"
                  spacing={`${avpLoginTokens.brand.gap}px`}
                  sx={{ mt: `${avpLoginTokens.brand.headerGap}px` }}
                >
                  <MainLogo
                    size={avpLoginTokens.brand.logoSize}
                    interactive={false}
                  />
                  <Typography
                    component="h1"
                    sx={{
                      m: 0,
                      fontFamily: designTokens.typography.fontFamily.roboto,
                      fontSize: `${avpLoginTokens.brand.productSize}px`,
                      fontWeight: 600,
                      lineHeight: "58px",
                      color: designTokens.fill.text.common100.dark,
                    }}
                  >
                    {loginTitle}
                  </Typography>
                </Stack>
              </>
            ) : (
              <>
                <Typography
                  sx={{
                    ...designTokens.typography.styles.display2,
                    mb: 0,
                  }}
                >
                  Welcome to
                </Typography>
                <Typography
                  sx={{
                    ...designTokens.typography.styles.display1,
                  }}
                >
                  {loginTitle}
                </Typography>
              </>
            )}
          </Box>

          {/* 로그인 옵션 */}
          <Box
            data-testid="login-options"
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: isMobile
                ? "36px"
                : isNativeAvpWebView
                  ? `${avpLoginTokens.content.sectionGap}px`
                  : designTokens.numbers.gap[64],
              alignItems: "center",
              width: isNativeAvpWebView
                ? `min(${avpLoginTokens.content.width}px, 100%)`
                : "100%",
            }}
          >
            {/* Studio code (Figma 9058-28751) + or + 소셜 */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                width: "100%",
                gap: isNativeAvpWebView
                  ? `${avpLoginTokens.content.authOptionsGap}px`
                  : designTokens.numbers.gap[32],
              }}
            >
              {showEmailPasswordForm && (
                <Box
                  component="form"
                  noValidate
                  onSubmit={handleEmailFormSubmit}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: isNativeAvpWebView
                      ? `${avpLoginTokens.content.formGap}px`
                      : 3,
                    width: "100%",
                  }}
                >
                  <Stack spacing={2}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: isNativeAvpWebView ? 2 : 1,
                      }}
                    >
                      <Typography
                        component="label"
                        htmlFor="login-email"
                        sx={{
                          color: designTokens.fill.text.grey90.dark,
                          fontSize: 17,
                          fontWeight: 400,
                          lineHeight: "20px",
                        }}
                      >
                        Email address
                      </Typography>
                      {/* Preserve the raw domain for the product ASCII-only validator.
                        `type=email` may present IDNs while exposing punycode. */}
                      <TextField
                        id="login-email"
                        type="text"
                        value={email}
                        disabled={emailStep !== "email"}
                        onChange={(event) => {
                          setEmail(event.target.value);
                          setEmailError("");
                        }}
                        onFocus={() => setFocusedInput("email")}
                        onBlur={() => {
                          setFocusedInput(null);
                          if (email && !isValidEmailAddress(email)) {
                            setEmailError(INVALID_EMAIL_MESSAGE);
                          }
                        }}
                        autoComplete="email"
                        slotProps={{
                          htmlInput: {
                            inputMode: "email",
                          },
                        }}
                        placeholder={
                          focusedInput === "email"
                            ? ""
                            : "Enter company email address"
                        }
                        error={Boolean(emailError)}
                        helperText={emailError || undefined}
                        fullWidth
                        sx={{
                          ...(isNativeAvpWebView
                            ? avpAuthInputSx
                            : authInputSx),
                          "& .MuiFormHelperText-root": {
                            mx: 0,
                            mt: 0.75,
                            color: isNativeAvpWebView
                              ? avpLoginTokens.input.errorColor
                              : designTokens.colors.accent.red.light,
                            fontFamily:
                              designTokens.typography.fontFamily.pretendard,
                          },
                        }}
                      />
                    </Box>
                    {emailStep !== "email" && (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        <Typography
                          component="label"
                          htmlFor="login-password"
                          sx={{
                            color: designTokens.fill.text.grey90.dark,
                            fontSize: 17,
                            fontWeight: 400,
                            lineHeight: "20px",
                          }}
                        >
                          Password
                        </Typography>
                        <TextField
                          id="login-password"
                          type={
                            isNativeAvpWebView
                              ? "password"
                              : showPassword
                                ? "text"
                                : "password"
                          }
                          value={password}
                          onChange={(event) => {
                            setPassword(event.target.value);
                            setPasswordError("");
                          }}
                          onFocus={() => setFocusedInput("password")}
                          onBlur={() => setFocusedInput(null)}
                          autoComplete={
                            emailStep === "create"
                              ? "new-password"
                              : "current-password"
                          }
                          placeholder={
                            focusedInput === "password"
                              ? ""
                              : emailStep === "create"
                                ? "Enter 8+ characters including a number & symbol"
                                : "Enter password"
                          }
                          error={Boolean(passwordError)}
                          helperText={passwordError || undefined}
                          fullWidth
                          sx={{
                            ...(isNativeAvpWebView
                              ? avpAuthInputSx
                              : authInputSx),
                            "& .MuiFormHelperText-root": {
                              mx: 0,
                              mt: 0.75,
                              color: isNativeAvpWebView
                                ? avpLoginTokens.input.errorColor
                                : designTokens.colors.accent.red.light,
                              fontFamily:
                                designTokens.typography.fontFamily.pretendard,
                            },
                          }}
                          slotProps={{
                            input: {
                              endAdornment: isNativeAvpWebView ? undefined : (
                                <InputAdornment position="end">
                                  <IconButton
                                    aria-label={
                                      showPassword
                                        ? "Hide password"
                                        : "Show password"
                                    }
                                    edge="end"
                                    onClick={() =>
                                      setShowPassword((visible) => !visible)
                                    }
                                    sx={{
                                      width: 24,
                                      height: 24,
                                      p: 0,
                                      mr: 0.5,
                                      color: designTokens.fill.text.grey60.dark,
                                      "&:hover": {
                                        bgcolor: "transparent",
                                        color:
                                          designTokens.fill.text.grey80.dark,
                                      },
                                    }}
                                  >
                                    <PasswordVisibilityIcon
                                      visible={showPassword}
                                    />
                                  </IconButton>
                                </InputAdornment>
                              ),
                            },
                          }}
                        />
                      </Box>
                    )}
                    {emailStep === "create" && (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        <Typography
                          component="label"
                          htmlFor="login-confirm-password"
                          sx={{
                            color: designTokens.fill.text.grey90.dark,
                            fontSize: 17,
                            fontWeight: 400,
                            lineHeight: "20px",
                          }}
                        >
                          Confirm password
                        </Typography>
                        <TextField
                          id="login-confirm-password"
                          type={
                            isNativeAvpWebView
                              ? "password"
                              : showPassword
                                ? "text"
                                : "password"
                          }
                          value={confirmPassword}
                          onChange={(event) => {
                            setConfirmPassword(event.target.value);
                            setConfirmPasswordError("");
                          }}
                          onFocus={() => setFocusedInput("confirmPassword")}
                          onBlur={() => setFocusedInput(null)}
                          autoComplete="new-password"
                          placeholder={
                            focusedInput === "confirmPassword"
                              ? ""
                              : "Re-enter password"
                          }
                          error={Boolean(confirmPasswordError)}
                          helperText={confirmPasswordError || undefined}
                          fullWidth
                          sx={{
                            ...(isNativeAvpWebView
                              ? avpAuthInputSx
                              : authInputSx),
                            "& .MuiFormHelperText-root": {
                              mx: 0,
                              mt: 0.75,
                              color: isNativeAvpWebView
                                ? avpLoginTokens.input.errorColor
                                : designTokens.colors.accent.red.light,
                              fontFamily:
                                designTokens.typography.fontFamily.pretendard,
                            },
                          }}
                        />
                      </Box>
                    )}
                  </Stack>
                  {emailStep === "create" && locationState.policy && (
                    <StudioPolicyConsent
                      id="studio-create-policy-consent"
                      checked={policyAccepted}
                      onChange={setPolicyAccepted}
                      policy={locationState.policy}
                    />
                  )}
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={
                      emailSubmitting ||
                      (!isNativeAvpWebView && !isEmailPasswordFormValid)
                    }
                    sx={
                      isNativeAvpWebView
                        ? avpAuthPrimaryButtonSx
                        : authPrimaryButtonSx
                    }
                  >
                    {emailStep === "email"
                      ? "Continue"
                      : emailSubmitting
                        ? emailStep === "create"
                          ? "Creating account"
                          : "Signing in"
                        : emailStep === "create"
                          ? "Create Account"
                          : "Sign in"}
                  </Button>
                  {emailStep !== "email" && (
                    <Button
                      type="button"
                      variant="text"
                      onClick={() => {
                        setEmailStep("email");
                        setPassword("");
                        setConfirmPassword("");
                        setPolicyAccepted(false);
                        setPasswordError("");
                        setConfirmPasswordError("");
                      }}
                      sx={
                        emailStep === "create"
                          ? authSecondaryActionSx
                          : {
                              textTransform: "none",
                              color: designTokens.fill.text.grey80.dark,
                            }
                      }
                    >
                      Sign in another way
                    </Button>
                  )}
                </Box>
              )}

              {/* 이메일·비밀번호 폼이 없는 AVP에서는 위쪽 구분선이 홀로 남으므로 함께 감춘다. */}
              {showEmailPasswordForm &&
                !isNativeAvpWebView &&
                emailStep === "email" && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "14px",
                      width: "100%",
                    }}
                  >
                    <Box sx={loginOrDividerLineSx} />
                    <Typography
                      sx={{
                        ...designTokens.typography.styles.label2,
                        color: designTokens.fill.text.grey60.dark,
                      }}
                    >
                      Or
                    </Typography>
                    <Box sx={loginOrDividerLineSx} />
                  </Box>
                )}

              {isNativeAvpWebView && emailStep === "email" && (
                <Box
                  data-testid="avp-sign-in-options"
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    width: "100%",
                    gap: `${avpLoginTokens.content.guestGap}px`,
                  }}
                >
                  <Box
                    data-testid="avp-login-methods"
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      width: "100%",
                      gap: `${avpLoginTokens.content.methodGap}px`,
                    }}
                  >
                    <SocialButton
                      backgroundColor={avpLoginTokens.social.background}
                      customIcon={
                        <VpnKeyOutlinedIcon
                          sx={{
                            fontSize: 24,
                            color: designTokens.fill.text.common100.dark,
                          }}
                        />
                      }
                      onClick={() => {
                        const target = new URL(
                          "/login/studio-code",
                          window.location.href,
                        );
                        notifyLoginShellTransitionBegin(target.href);
                        navigate("/login/studio-code");
                      }}
                    >
                      Sign in with View R Studio code
                    </SocialButton>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "9px",
                        width: "100%",
                        maxWidth: 467,
                        justifyContent: "center",
                      }}
                    >
                      <Box sx={loginOrDividerLineSx} />
                      <Typography
                        sx={{
                          fontFamily:
                            designTokens.typography.fontFamily.pretendard,
                          fontSize: "20px",
                          fontWeight: 500,
                          lineHeight: 1,
                          color: designTokens.fill.text.grey80.dark,
                        }}
                      >
                        or
                      </Typography>
                      <Box sx={loginOrDividerLineSx} />
                    </Box>
                    {providerLoginButtons}
                  </Box>

                  {showGuestLogin && (
                    <Button
                      data-testid="avp-guest-login"
                      variant="text"
                      onClick={() => void handleLoginGuest()}
                      disabled={guestSubmitting}
                      sx={{
                        minWidth: 0,
                        width: "100%",
                        minHeight: avpLoginTokens.guest.height,
                        p: 0,
                        color: designTokens.fill.text.common100.dark,
                        fontFamily: designTokens.typography.fontFamily.roboto,
                        fontSize: avpLoginTokens.guest.fontSize,
                        fontWeight: avpLoginTokens.guest.fontWeight,
                        lineHeight: "24px",
                        letterSpacing: 0,
                        textTransform: "none",
                        "&:hover": {
                          color: designTokens.fill.text.common100.dark,
                          backgroundColor: "transparent",
                          textDecoration: "underline",
                        },
                        "&.Mui-disabled": {
                          color: designTokens.fill.text.grey60.dark,
                        },
                      }}
                    >
                      Get started as a guest
                    </Button>
                  )}
                </Box>
              )}

              {!isNativeAvpWebView &&
                emailStep === "email" &&
                providerLoginButtons}
            </Box>

            {/* 약관 안내 */}
            <Box
              data-testid="auth-policy-notice"
              textAlign="center"
              sx={{
                display: emailStep === "create" ? "none" : undefined,
                width: isMobile || isNativeAvpWebView ? "100%" : 336,
                minHeight: isNativeAvpWebView
                  ? avpLoginTokens.policy.lineHeight
                  : undefined,
                lineHeight: 0,
              }}
            >
              {!isNativeAvpWebView && (
                <Typography
                  sx={{
                    ...designTokens.typography.styles.headline1,
                    color: designTokens.fill.text.grey80.dark,
                    ...(isMobile
                      ? {
                          fontFamily: '"Roboto", sans-serif',
                          fontSize: "14px",
                          lineHeight: "20px",
                        }
                      : {}),
                  }}
                >
                  By connecting your account, you agree
                </Typography>
              )}
              <Typography
                component="div"
                sx={{
                  ...designTokens.typography.styles.headline1,
                  color: designTokens.fill.text.grey80.dark,
                  ...(isNativeAvpWebView
                    ? {
                        fontFamily: designTokens.typography.fontFamily.roboto,
                        fontSize: `${avpLoginTokens.policy.fontSize}px`,
                        fontWeight: 400,
                        lineHeight: avpLoginTokens.policy.lineHeight,
                      }
                    : isMobile
                      ? {
                          fontFamily: '"Roboto", sans-serif',
                          fontSize: "14px",
                          lineHeight: "20px",
                        }
                      : {}),
                }}
              >
                {!isNativeAvpWebView && <>to our </>}
                <Typography
                  component={isNativeAvpWebView ? "span" : "a"}
                  href={
                    isNativeAvpWebView
                      ? undefined
                      : "https://viewr.co/terms-of-service"
                  }
                  target={isNativeAvpWebView ? undefined : "_blank"}
                  rel={isNativeAvpWebView ? undefined : "noopener noreferrer"}
                  onClick={
                    isNativeAvpWebView ? () => setOpenTerms(true) : undefined
                  }
                  sx={{
                    color: designTokens.fill.text.grey80.dark,
                    fontFamily: isNativeAvpWebView
                      ? designTokens.typography.fontFamily.roboto
                      : (designTokens.typography.styles.headline1 as TextStyle)
                          .fontFamily,
                    fontSize: isNativeAvpWebView
                      ? `${avpLoginTokens.policy.fontSize}px`
                      : (designTokens.typography.styles.headline1 as TextStyle)
                          .fontSize,
                    fontWeight: isNativeAvpWebView
                      ? 400
                      : (designTokens.typography.styles.headline1 as TextStyle)
                          .fontWeight,
                    lineHeight: isNativeAvpWebView
                      ? avpLoginTokens.policy.lineHeight
                      : (designTokens.typography.styles.headline1 as TextStyle)
                          .lineHeight,
                    textDecoration: "underline",
                    cursor: "pointer",
                    "&:hover": {
                      opacity: 0.8,
                    },
                  }}
                >
                  Terms of Service
                </Typography>
                {isNativeAvpWebView ? " · " : " and "}
                <Typography
                  component={isNativeAvpWebView ? "span" : "a"}
                  href={
                    isNativeAvpWebView
                      ? undefined
                      : "https://viewr.co/privacy-policy"
                  }
                  target={isNativeAvpWebView ? undefined : "_blank"}
                  rel={isNativeAvpWebView ? undefined : "noopener noreferrer"}
                  onClick={
                    isNativeAvpWebView ? () => setOpenPrivacy(true) : undefined
                  }
                  sx={{
                    color: designTokens.fill.text.grey80.dark,
                    fontFamily: isNativeAvpWebView
                      ? designTokens.typography.fontFamily.roboto
                      : (designTokens.typography.styles.headline1 as TextStyle)
                          .fontFamily,
                    fontSize: isNativeAvpWebView
                      ? `${avpLoginTokens.policy.fontSize}px`
                      : (designTokens.typography.styles.headline1 as TextStyle)
                          .fontSize,
                    fontWeight: isNativeAvpWebView
                      ? 400
                      : (designTokens.typography.styles.headline1 as TextStyle)
                          .fontWeight,
                    lineHeight: isNativeAvpWebView
                      ? avpLoginTokens.policy.lineHeight
                      : (designTokens.typography.styles.headline1 as TextStyle)
                          .lineHeight,
                    textDecoration: "underline",
                    cursor: "pointer",
                    "&:hover": {
                      opacity: 0.8,
                    },
                  }}
                >
                  Privacy Policy
                </Typography>
              </Typography>
            </Box>
          </Box>

          {!isNativeAvpWebView && showGuestLogin && emailStep === "email" && (
            <Button
              variant="text"
              onClick={() => void handleLoginGuest()}
              disabled={guestSubmitting}
              sx={{
                minWidth: 0,
                minHeight: 23,
                p: 0,
                color: designTokens.fill.text.grey90.dark,
                fontFamily: '"Roboto", sans-serif',
                fontSize: 20,
                fontWeight: 400,
                lineHeight: "23px",
                letterSpacing: 0,
                textTransform: "none",
                "&:hover": {
                  color: designTokens.fill.text.common100.dark,
                  backgroundColor: "transparent",
                  textDecoration: "underline",
                },
                "&.Mui-disabled": {
                  color: designTokens.fill.text.grey60.dark,
                },
              }}
            >
              Get started as a guest
            </Button>
          )}
        </Box>
      </Box>

      {/* Privacy 팝업 */}
      <Privacy open={openPrivacy} onClose={() => setOpenPrivacy(false)} />

      {/* Terms 팝업 */}
      <Terms open={openTerms} onClose={() => setOpenTerms(false)} />

      {/* Toast 알림 */}
      <Toast
        variant="chrome"
        message="This site is optimized for Chrome."
        open={showBrowserRecommendationToast}
        onClose={() => setShowBrowserRecommendationToast(false)}
        duration={null}
        actionLabel="Download Chrome"
        onAction={handleDownloadChrome}
      />

      <Toast
        type={toastState.type}
        message={toastState.message}
        open={toastState.open}
        onClose={handleToastClose}
        duration={3000}
      />
    </Box>
  );
};

export default LoginPage;
